/* global Plaid */
import { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import useAch from './useAch';
import useScript from './useScript';
import useLeaveSurveyModal from './useLeaveSurveryModal';

export const usePlaid = () => {
  const { t } = useTranslation();
  const { uuid } = useParams();
  const client = t('companyNameShort');
  const [loaded, error] = useScript('https://cdn.plaid.com/link/v2/stable/link-initialize.js');
  const { createPlaidLinkToken, exchangePublicToken } = useAch();
  const { disableSurvey, resetSurvey } = useLeaveSurveyModal();

  /**
   * Disables exit dialog and survey when interacting with Plaid
   */
  useEffect(() => {
    disableSurvey();

    return () => resetSurvey();
  }, [disableSurvey, resetSurvey]);

  /**
   * - generate linkToken from BE (createPlaidLinkToken)
   * - pass linkToken to Plaid.create() receive public token
   * - exchange publicToken for an access token (exchangePublicToken)
   * - call onSuccess passing the accessToken and metaData
   *
   * If an itemUuid is present we will skip exchanging public token
   * as the backend already has the access token and we can use the update flow.
   */

  /**
   *
   * @param {Object} config Plaid create config object
   * @param {Function} config.onExit onExit function called when user exits Plaid Link
   * @param {Function} [config.onLoad] onLoad function called when Plaid Link has loaded
   * @param {Function} [config.onSuccess] function called when item is successfully onboarded
   * @param {Function} [config.onEvent] function called when an event is emitted
   * @param {String} [config.itemUuid] specify itemUuid when you want to update an existing item
   */
  const create = async ({
    onLoad = () => console.log('loaded plaid link'),
    onExit,
    onSuccess: passedInOnSuccess = null,
    onEvent = (eventName, metaData) => console.log(eventName, metaData),
    itemUuid = null,
  }) => {
    const linkToken = await createPlaidLinkToken({
      itemUuid,
      clientName: client,
      redirectURI: process.env.REACT_APP_PLAID_REDIRECT_URI,
      // product here needs to be lowercase
      products: ['assets', 'auth'],
    });

    localStorage.setItem('link_token', linkToken);
    localStorage.setItem('personalLoan_uuid', uuid);

    const onSuccess = async (public_token, metaData) => {
      let accessToken = null;
      const setToken = async () => {
        accessToken = await exchangePublicToken(public_token);
      };

      if (!itemUuid) await setToken();
      if (passedInOnSuccess)
        passedInOnSuccess(accessToken, {
          ...metaData,
          // product here needs to be uppercase
          products: ['ASSETS', 'AUTH'],
        });
    };

    Plaid.create({
      token: linkToken,
      onLoad,
      onExit,
      onSuccess,
      onEvent,
    }).open();
  };

  const reinitialize = useCallback(
    async ({ onExit, onSuccess: passedInOnSuccess = null }) => {
      const linkToken = localStorage.getItem('link_token');
      const onSuccess = async (public_token, metaData) => {
        const accessToken = await exchangePublicToken(public_token);
        if (passedInOnSuccess) passedInOnSuccess(accessToken, metaData);
      };

      Plaid.create({
        token: linkToken,
        receivedRedirectUri: window.location.href,
        onSuccess,
        onExit,
      }).open();
    },
    [exchangePublicToken]
  );

  return { loaded, error, create, reinitialize };
};

export default usePlaid;
